import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PushNotificationActionCreator, { IPushSubscriptionState } from '../actions/pushNotificationActionCreator';
import { IPushNotificationState } from '../reducers/pushNotificationReducer';
import pushNotificationService from '../services/pushNotificationService';
import pushNotificationApiService, { IPushTopicSubscription } from '../services/pushNotificationApiService';
import { useFeature, useIsAuthenticated } from '../selectors/global';
import { FeatureFlag } from '../../../gameConfig/sharedTypes';

const MERGE_DISMISSED_KEY = 'rio_push_merge_dismissed';

export interface IUsePushNotificationResult {
  isSupported: boolean;
  isInitialized: boolean;
  permissionState: NotificationPermission;
  subscription: IPushSubscriptionState | null;
  topics: IPushTopicSubscription[];
  isLoading: boolean;
  error: string | null;
  shouldPromptMerge: boolean;
  initialize: () => Promise<void>;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
  subscribeToTopic: (topic: string, config?: Record<string, unknown>) => Promise<void>;
  unsubscribeFromTopic: (topic: string) => Promise<void>;
  mergeSubscription: () => Promise<void>;
  dismissMergePrompt: () => void;
}

export function usePushNotification(): IUsePushNotificationResult {
  const dispatch = useDispatch();
  const state = useSelector((rootState: any) => rootState.pushNotification) as IPushNotificationState;
  const isAuthenticated = !!useIsAuthenticated();
  const isFeatureEnabled = useFeature(FeatureFlag.PUSH_NOTIFICATIONS_UI);
  const initializedRef = useRef(false);
  const [mergeDismissed, setMergeDismissed] = useState(() =>
    typeof window !== 'undefined' && sessionStorage.getItem(MERGE_DISMISSED_KEY) === '1'
  );

  const initialize = useCallback(async () => {
    if (initializedRef.current) {
      return;
    }
    initializedRef.current = true;

    try {
      const isSupported = isFeatureEnabled && pushNotificationService.isSupported();
      dispatch(PushNotificationActionCreator.setSupported(isSupported));
      if (!isSupported) {
        return;
      }

      const permissionState = pushNotificationService.getPermissionState();
      dispatch(PushNotificationActionCreator.setPermissionState(permissionState));
      if (permissionState !== 'granted') {
        return;
      }

      try {
        const browserSubscription = await pushNotificationService.getCurrentSubscription();
        if (!browserSubscription) {
          return;
        }

        const { endpoint } = pushNotificationService.extractSubscriptionData(browserSubscription);
        const response = await pushNotificationApiService.getSubscription(endpoint);

        if (response.subscription) {
          dispatch(PushNotificationActionCreator.setSubscription(response.subscription));
          dispatch(PushNotificationActionCreator.setTopics(response.topics));
        }
      } catch {
        // Subscription might not exist on backend yet
      }
    } finally {
      dispatch(PushNotificationActionCreator.setInitialized(true));
    }
  }, [dispatch, isFeatureEnabled]);

  const subscribe = useCallback(async () => {
    if (!state.isSupported) {
      dispatch(PushNotificationActionCreator.setError('Browser notifications are not supported'));
      return;
    }

    dispatch(PushNotificationActionCreator.setLoading(true));

    try {
      let permission = pushNotificationService.getPermissionState();
      if (permission === 'default') {
        let timeoutId: ReturnType<typeof setTimeout>;
        permission = await Promise.race([
          pushNotificationService.requestPermission().then((p) => {
            clearTimeout(timeoutId);
            return p;
          }),
          new Promise<NotificationPermission>((resolve) => {
            timeoutId = setTimeout(() => resolve('denied'), 60000);
          })
        ]);
        dispatch(PushNotificationActionCreator.setPermissionState(permission));
      }

      if (permission !== 'granted') {
        dispatch(PushNotificationActionCreator.setPermissionState(permission));
        return;
      }

      const { publicKey } = await pushNotificationApiService.getVapidKey();
      const browserSubscription = await pushNotificationService.subscribeToPush(publicKey);
      const { endpoint, keys } = pushNotificationService.extractSubscriptionData(browserSubscription);
      const response = await pushNotificationApiService.subscribe(endpoint, keys);

      // Backend already seeds default topics; just fetch the full subscription state.
      const subscriptionDetails = await pushNotificationApiService.getSubscription(endpoint);

      dispatch(
        PushNotificationActionCreator.setSubscription({
          id: response.subscriptionId,
          isLinked: subscriptionDetails.subscription?.isLinked ?? isAuthenticated
        })
      );
      dispatch(PushNotificationActionCreator.setTopics(subscriptionDetails.topics));
    } catch (err: any) {
      dispatch(PushNotificationActionCreator.setError(err.message || 'Failed to subscribe'));
    } finally {
      dispatch(PushNotificationActionCreator.setLoading(false));
    }
  }, [dispatch, state.isSupported, isAuthenticated]);

  const unsubscribe = useCallback(async () => {
    dispatch(PushNotificationActionCreator.setLoading(true));

    try {
      const browserSubscription = await pushNotificationService.getCurrentSubscription();
      if (browserSubscription) {
        const { endpoint, keys } = pushNotificationService.extractSubscriptionData(browserSubscription);
        await pushNotificationApiService.unsubscribe(endpoint, keys.auth);
        await browserSubscription.unsubscribe();
      }

      dispatch(PushNotificationActionCreator.setSubscription(null));
      dispatch(PushNotificationActionCreator.setTopics([]));
    } catch (err: any) {
      dispatch(PushNotificationActionCreator.setError(err.message || 'Failed to unsubscribe'));
    } finally {
      dispatch(PushNotificationActionCreator.setLoading(false));
    }
  }, [dispatch]);

  const getEndpoint = useCallback(async (): Promise<string | null> => {
    const browserSubscription = await pushNotificationService.getCurrentSubscription();
    if (!browserSubscription) {
      return null;
    }
    return pushNotificationService.extractSubscriptionData(browserSubscription).endpoint;
  }, []);

  const subscribeToTopic = useCallback(
    async (topic: string, config?: Record<string, unknown>) => {
      if (!state.subscription) {
        dispatch(PushNotificationActionCreator.setError('Not subscribed to browser notifications'));
        return;
      }

      try {
        const endpoint = await getEndpoint();
        if (!endpoint) {
          dispatch(PushNotificationActionCreator.setError('Push subscription not found'));
          return;
        }
        await pushNotificationApiService.subscribeToTopic(state.subscription.id, topic, endpoint, config);
        dispatch(PushNotificationActionCreator.addTopic({ topic, config: config || null }));
      } catch (err: any) {
        dispatch(PushNotificationActionCreator.setError(err.message || 'Failed to subscribe to topic'));
      }
    },
    [dispatch, state.subscription, getEndpoint]
  );

  const unsubscribeFromTopic = useCallback(
    async (topic: string) => {
      if (!state.subscription) {
        return;
      }

      try {
        const endpoint = await getEndpoint();
        if (!endpoint) {
          return;
        }
        await pushNotificationApiService.unsubscribeFromTopic(state.subscription.id, topic, endpoint);
        dispatch(PushNotificationActionCreator.removeTopic(topic));
      } catch (err: any) {
        dispatch(PushNotificationActionCreator.setError(err.message || 'Failed to unsubscribe from topic'));
      }
    },
    [dispatch, state.subscription, getEndpoint]
  );

  const mergeSubscription = useCallback(async () => {
    try {
      const endpoint = await getEndpoint();
      if (!endpoint) {
        return;
      }
      await pushNotificationApiService.mergeSubscription(endpoint);
      dispatch(PushNotificationActionCreator.setLinked(true));
    } catch (err: any) {
      dispatch(PushNotificationActionCreator.setError(err.message || 'Failed to link subscription'));
    }
  }, [dispatch, getEndpoint]);

  const dismissMergePrompt = useCallback(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(MERGE_DISMISSED_KEY, '1');
    }
    setMergeDismissed(true);
  }, []);

  const isLinked = state.subscription?.isLinked ?? false;

  const shouldPromptMerge = useMemo(() => {
    if (!isAuthenticated || !state.subscription || isLinked || mergeDismissed) {
      return false;
    }
    return true;
  }, [isAuthenticated, state.subscription, isLinked, mergeDismissed]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  return {
    isSupported: state.isSupported,
    isInitialized: state.isInitialized,
    permissionState: state.permissionState,
    subscription: state.subscription,
    topics: state.topics,
    isLoading: state.isLoading,
    error: state.error,
    shouldPromptMerge,
    initialize,
    subscribe,
    unsubscribe,
    subscribeToTopic,
    unsubscribeFromTopic,
    mergeSubscription,
    dismissMergePrompt
  };
}
